'use client'

import React from 'react'

// hooks 
import { useUserContext } from "@/components/providers"
import { useAppHotkeys } from "@/hooks/use-hotkeys"

// utils 
import { cn } from "@/lib/utils"

// types
import { SendNewMessage, Message, UsePromptFormReturn } from "@/lib/types"

// components 
import { PromptAreaFileUpload } from "@/components/prompt-area/prompt-area-file-upload"
import { PromptAreaDebug } from "@/components/prompt-area/prompt-area-debug"
import { Form, FormField } from "@/components/ui/form"
import { Button } from "@/components/ui/button"
import { PromptAreaTextarea } from "@/components/prompt-area/prompt-area-textarea"
import { ModelSelectorContainer } from "@/components/prompt-area/model-selector"
import { FileDisplay } from "@/components/filesV2/file-display"
import { motion, AnimatePresence } from "motion/react"
import { Send, Upload, X } from "lucide-react"
import { PromptAreaModeSelector } from "@/components/prompt-area/prompt-area-mode-selector"
import { PromptAreaFileDisplay } from "@/components/prompt-area/prompt-area-file-display"
import { AccessGate } from "../auth/access-gate"
import { AnimatedButton } from "@/components/ui/animated-button"
import { PromptAreaOptions } from '@/components/prompt-area/prompt-area-options'
import { toast } from 'sonner'
import { PromptAreaText } from '@/components/prompt-area/prompt-area-mode-text'
import { PromptAreaImage } from '@/components/prompt-area/prompt-area-mode-image'


export function PromptAreaForm({
    promptForm,
    messageCount,
    requestInProgress,
    conversationSystemPrompt,
    setConversationSystemPrompt
}: {
    promptForm: UsePromptFormReturn
    messageCount: number
    requestInProgress: boolean
    conversationSystemPrompt: string | null
    setConversationSystemPrompt: (value: string | null) => void
    }) {
    const { userContextValue } = useUserContext()


    // Handle escape key for canceling edit using hotkeys hook
    useAppHotkeys(
        {
            'escape': promptForm.handleCancelEdit
        },
        {
            enabled: promptForm.isEditing,
            excludeInputs: false // Allow escape to work even when in inputs
        }
    )

    return (
        <>
            {/* Click outside overlay when editing */}
            <AnimatePresence>
                {promptForm.isEditing && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ 
                            duration: 0.2,
                            ease: [0.25, 0.46, 0.45, 0.94] // ease-out-quad
                        }}
                        className="absolute inset-0 z-10 bg-background/50"
                        onClick={promptForm.handleCancelEdit}
                    />
                )}
            </AnimatePresence>

            <Form {...promptForm.form}>
                <motion.form
                    onSubmit={promptForm.form.handleSubmit(promptForm.onSubmit)}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, ease: 'easeInOut' }}
                    className={cn(
                        'w-full flex flex-col gap-2 relative z-20', 
                        userContextValue?.settings.maxWidth 
                            ? `max-w-[${userContextValue.settings.maxWidth}px]` 
                            : 'max-w-[800px]',
                        messageCount === 0 && 'px-[5%]'
                    )}
                >
                    {messageCount === 0 && (
                        <PromptAreaModeSelector
                            promptForm={promptForm}
                        />
                    )}

                    {promptForm.currentMode === 'text' && (
                        <PromptAreaFileDisplay
                            promptForm={promptForm}
                        />
                    )}

                    {/* Cancel badge animation */}
                    <AnimatePresence>
                        {promptForm.isEditing && (
                            <motion.div
                                initial={{ opacity: 0, transform: "translateX(-50%) translateY(8px) scale(0.95)" }}
                                animate={{ opacity: 1, transform: "translateX(-50%) translateY(-10px) scale(1)" }}
                                exit={{ opacity: 0, transform: "translateX(-50%) translateY(8px) scale(0.95)" }}
                                transition={{ 
                                    type: "spring",
                                    damping: 15,
                                    stiffness: 350,
                                    mass: 0.8
                                }}
                                className="absolute -top-12 left-1/2 z-10 cursor-pointer"
                                onClick={promptForm.handleCancelEdit}
                            >
                                <div className="bg-background border border-border rounded-lg px-3 py-1.5 shadow-lg flex items-center gap-2">
                                    <X className="h-3.5 w-3.5 text-muted-foreground" />
                                    <span className="text-sm text-foreground font-medium">
                                        Cancel • <kbd className="px-1.5 py-0.5 bg-muted rounded text-xs text-muted-foreground">Esc</kbd>
                                    </span>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {promptForm.currentMode === 'text'
                        ? <PromptAreaText promptForm={promptForm} requestInProgress={requestInProgress} />
                        : <PromptAreaImage promptForm={promptForm} requestInProgress={requestInProgress} />}

                    <AccessGate developerFeature='debugPromptArea'>
                        <PromptAreaDebug promptForm={promptForm} />
                    </AccessGate>
                </motion.form>
            </Form>
        </>
    )
}   